// =========================================================================
// IncidentsScene — each closed incident told twice. Fork-owned (zone C).
//
// Port of `renderIncident(id)` at line 2322 of
// `/Users/timwark/Projects/Data/OEM+/OEM+_Platform_Demo.html`, with the
// `#v-incident` section markup at line ~1425 and the `.tl` / `.tl.replay` /
// `.kpi` CSS (lines 115–172).
//
// The page is the argument for the whole platform: on the left, what happened,
// with the emails and reports that were written at the time; on the right, the
// same days replayed through the agent chain on data that already existed; and
// between them, the cost of the difference. Every figure on the KPI row is a
// field off the incident's work order, so the comparison is auditable rather
// than asserted.
//
// The demo pairs the chart with `turnStill(...)`, the machine still with the
// failed component highlighted (line 2327). Here that is
// `shared/viz/IssueMachinePicture` (L7's `TurntableStill` inside) at the panel
// size, heading the component panel above the demo's caption, linked to the
// machine page.
//
// UI layer. Costs come from `incidentCosts()` in the engine; part resolution,
// series reading and the cost comparison come from the sibling transform.
//
// @see docs/logic/issues/incidents.md
// =========================================================================

import { useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  HelpAnchor,
  Kicker,
  SkeletonBlock,
  SurfacePageBody,
  SurfacePageLayout,
} from '@/design-system';
import { AskStrip } from '@/versions/v1/chrome-oem/AskStrip';
import { Commentable } from '@/shared/comments/Commentable';
import { cn } from '@/shared/cn';
import { BY_ID, MINES } from '@/versions/v1/data/core';
import { unpackTable } from '@/versions/v1/shared/columnar';
import {
  fmt$,
  incidentCosts,
  incidentPickerEntries,
  MSHORT,
} from '@/versions/v1/shared/oem-engine';
import type { MineCode } from '@/versions/v1/schema/machine';
import { useOemData, type DailyMetricRow, type WorkOrder } from '@/versions/v1/shared/useOemData';
import { LineChart } from '@/versions/v1/shared/viz/LineChart';
import { IssueMachinePicture } from '@/versions/v1/shared/viz/IssueMachinePicture';
import { emphasisSegments } from './AlertsScene.transform';
import {
  chartLabels,
  costComparison,
  documentHref,
  kpiCalc,
  momentKey,
  renderKpiValue,
  renderParts,
  resolveIncident,
  resolveSeries,
  type SeriesContext,
} from './IncidentsScene.transform';
import { useSourceDrawer } from './useSourceDrawer';

/** One sentence (simplification pass B4). */
const DESCRIPTION =
  'Each incident twice: what happened, and the same days replayed through the agent chain on the data that already existed.';

export function IncidentsScene() {
  const { incidentId } = useParams<{ incidentId: string }>();
  const incident = useMemo(() => resolveIncident(incidentId), [incidentId]);
  const picker = useMemo(() => incidentPickerEntries(), []);

  /**
   * C1, as on `AlertsScene`. `/issues/incidents/:incidentId` lands on the
   * incident; `/issues/incidents` with no id is still the list, because that
   * is what the nav row means.
   */
  const onList = !incidentId;
  const others = useMemo(
    () => picker.filter((entry) => entry.incident.id !== incident.id),
    [picker, incident.id],
  );
  const { openDataset, pending } = useSourceDrawer();

  const workOrdersData = useOemData('work-orders');
  const telemetryData = useOemData('telemetry');

  const workOrders = useMemo<readonly WorkOrder[]>(
    () => (workOrdersData.data ? unpackTable<WorkOrder>(workOrdersData.data.wos) : []),
    [workOrdersData.data],
  );

  const costs = useMemo(
    () => incidentCosts(incident, { workOrders }),
    [incident, workOrders],
  );
  const comparison = useMemo(() => costComparison(incident, costs), [incident, costs]);

  const seriesCtx = useMemo<SeriesContext>(
    () => ({
      dailyMetric: telemetryData.data
        ? unpackTable<DailyMetricRow>(telemetryData.data.daily_metric)
        : [],
      incidentSeries: telemetryData.data?.incident_series ?? {},
    }),
    [telemetryData.data],
  );

  const labels = useMemo(
    () => chartLabels(incident.chart, seriesCtx),
    [incident.chart, seriesCtx],
  );
  const plotted = useMemo(
    () =>
      incident.chart.series.map((series) => ({
        values: resolveSeries(series.source, seriesCtx).values,
        color: series.colour,
        area: series.area,
        dots: series.dots,
      })),
    [incident.chart.series, seriesCtx],
  );

  const chartReady = !telemetryData.loading && labels.length > 0;
  const machine = BY_ID[incident.machine];

  return (
    <SurfacePageLayout width="full">
      <Commentable name="IncidentsScene">
        <div className="contents">
          <SurfacePageBody className="space-y-4" as="div">
            <Commentable name="IncidentsHeader">
              <header className="flex flex-wrap items-start justify-between gap-4">
                <div className="min-w-0">
                  <h1 className="flex flex-wrap items-center gap-x-3 gap-y-2 text-2xl font-semibold tracking-tight text-[var(--ink-primary)]">
                    {onList ? 'Incidents' : incident.title}
                    {onList ? null : (
                      <span className="rounded-full bg-[var(--surface-2)] px-2.5 py-1 text-xs font-medium text-[var(--ink-secondary)]">
                        {incident.status}
                      </span>
                    )}
                  </h1>
                  <p className="mt-2 max-w-2xl text-sm leading-relaxed text-[var(--ink-secondary)]">
                    {onList
                      ? DESCRIPTION
                      : `${incident.ref} · ${incident.machine} · ${incident.date}`}
                  </p>
                </div>
                <div className="flex shrink-0 flex-col items-end gap-1.5">
                  <span className="mono rounded-full bg-[var(--surface-2)] px-2.5 py-1 text-xs text-[var(--ink-secondary)]">
                    {picker.length} closed this fortnight
                  </span>
                  <HelpAnchor doc="issues/incidents" />
                </div>
              </header>
            </Commentable>

            <AskStrip view="incident" />

            {/* C1: the three-card grid is the list page, not the drill-down. */}
            {onList ? (
            <Commentable name="IncidentPicker">
              <nav aria-label="Incidents" className="grid gap-2.5 md:grid-cols-3">
                {picker.map((entry) => {
                  const on = entry.incident.id === incident.id;
                  return (
                    <Link
                      key={entry.incident.id}
                      to={`/issues/incidents/${entry.incident.id}`}
                      aria-current={on ? 'page' : undefined}
                      className={cn(
                        'grid content-start gap-1 rounded-xl border p-4 text-left transition-colors',
                        on
                          ? 'border-[var(--accent)] bg-[var(--surface-1)]'
                          : 'border-[var(--hairline)] bg-[var(--surface-1)] hover:border-[var(--accent)]',
                      )}
                    >
                      <span className="mono justify-self-start rounded bg-[var(--surface-2)] px-1.5 py-0.5 text-[9.5px] font-medium uppercase tracking-[0.04em] text-[var(--ink-secondary)]">
                        {entry.tag}
                      </span>
                      <b className="text-[13px] font-semibold leading-snug text-[var(--ink-primary)]">
                        {entry.incident.title}
                      </b>
                      <small className="text-[11.5px] text-[var(--ink-tertiary)]">
                        {entry.incident.date} · {entry.site} · {entry.incident.machine} (
                        {entry.machine.model})
                      </small>
                      <span className="mono mt-0.5 text-[11px] text-[var(--accent)]">
                        {entry.incident.ref}
                      </span>
                    </Link>
                  );
                })}
              </nav>
            </Commentable>
            ) : null}

            {onList ? null : (
            <Commentable name="IncidentPage">
              <article className="space-y-4">
                <header className="rounded-xl border border-[var(--hairline)] bg-[var(--surface-1)] p-4">
                  {/*
                    C1 moved the ref, the machine and the status into the H1
                    and its meta line, so the article header keeps the way out
                    to the machine and the incident's own sentence.
                  */}
                  <div className="flex flex-wrap items-center gap-2.5">
                    <Link
                      to={`/machines/${incident.machine}`}
                      className="mono ml-auto text-[11px] font-medium text-[var(--accent)] hover:underline"
                    >
                      Open {incident.machine} · {machine?.model} ›
                    </Link>
                  </div>
                  <p className="mt-2 max-w-[100ch] text-[12.5px] leading-relaxed text-[var(--ink-secondary)]">
                    {incident.sub}
                  </p>
                </header>

                {/* KPI row */}
                <Commentable name="IncidentKpis">
                  <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                    {incident.kpis.map((kpi) => {
                      const figure = renderKpiValue(kpi.value, costs.workOrder);
                      return (
                        <div
                          key={kpi.label}
                          className="rounded-xl border border-[var(--hairline)] bg-[var(--surface-1)] p-4"
                        >
                          <Kicker className="block">{kpi.label}</Kicker>
                          <div className="mt-1.5 flex flex-wrap items-baseline gap-1.5">
                            <span
                              className={cn(
                                'mono text-2xl font-semibold leading-tight',
                                figure.critical ? '' : 'text-[var(--ink-primary)]',
                              )}
                              style={figure.critical ? { color: 'var(--crit)' } : undefined}
                            >
                              {figure.text}
                            </span>
                            <span className="text-xs text-[var(--ink-tertiary)]">{kpi.unit}</span>
                          </div>
                          <p className="mt-1.5 text-[11.5px] leading-relaxed text-[var(--ink-tertiary)]">
                            {renderParts(kpi.note, costs.workOrder)}
                          </p>
                          {kpi.calc ? (
                            <p className="mt-1.5 border-t border-[var(--hairline)] pt-1.5 text-[10.5px] leading-relaxed text-[var(--ink-tertiary)]">
                              <span className="font-medium">How it is worked out: </span>
                              {renderParts(kpi.calc, costs.workOrder)}
                            </p>
                          ) : null}
                          {kpi.source ? (
                            <button
                              type="button"
                              aria-busy={pending === kpi.source.dataset}
                              onClick={() =>
                                openDataset(kpi.source!.dataset, {
                                  filter: kpi.source!.filter,
                                  calc: kpiCalc(kpi, costs.workOrder),
                                })
                              }
                              className="mt-1.5 text-[11px] font-medium text-[var(--accent)] underline decoration-dotted underline-offset-2 hover:no-underline"
                            >
                              source rows ›
                            </button>
                          ) : null}
                        </div>
                      );
                    })}
                  </div>
                </Commentable>

                {/* chart + component panel */}
                <div className="grid gap-4 lg:grid-cols-[minmax(0,1.35fr)_minmax(0,1fr)]">
                  <Commentable name="IncidentChart">
                    <section className="min-w-0 rounded-xl border border-[var(--hairline)] bg-[var(--surface-1)] p-4">
                      <h3 className="text-[13px] font-semibold text-[var(--ink-primary)]">
                        {incident.chart.title}
                      </h3>
                      <p className="mb-3 mt-0.5 text-[11.5px] text-[var(--ink-tertiary)]">
                        {incident.chart.subText}
                        <button
                          type="button"
                          aria-busy={pending === incident.chart.subSource.dataset}
                          onClick={() =>
                            openDataset(incident.chart.subSource.dataset, {
                              filter: incident.chart.subSource.filter,
                            })
                          }
                          className="font-medium text-[var(--accent)] underline decoration-dotted underline-offset-2 hover:no-underline"
                        >
                          source rows
                        </button>
                      </p>
                      {chartReady ? (
                        <LineChart
                          labels={labels}
                          series={plotted}
                          vline={incident.chart.opts.vline}
                          vlabel={incident.chart.opts.vlabel}
                          hline={incident.chart.opts.hline}
                          hlabel={incident.chart.opts.hlabel}
                          xTitle={incident.chart.opts.xTitle}
                          yTitle={incident.chart.opts.yTitle}
                          dec={incident.chart.opts.dec}
                          min={incident.chart.opts.min}
                          ariaLabel={incident.chart.title}
                        />
                      ) : (
                        <SkeletonBlock className="h-[210px] rounded-lg" />
                      )}
                    </section>
                  </Commentable>

                  <aside className="min-w-0 rounded-xl border border-[var(--hairline)] bg-[var(--surface-1)] p-4">
                    <Link
                      to={`/machines/${incident.machine}`}
                      title={`Open ${incident.machine} — equipment details`}
                      className="mb-3 block rounded-lg border border-[var(--hairline)] transition-colors hover:border-[var(--accent)]"
                    >
                      <IssueMachinePicture
                        machineId={incident.machine}
                        highlight={incident.comp}
                        size="panel"
                      />
                    </Link>
                    <Kicker className="block">Component</Kicker>
                    <div className="mt-1.5 text-sm font-medium text-[var(--ink-primary)]">
                      {incident.comp}
                    </div>
                    <p className="mt-2 text-[11px] leading-relaxed text-[var(--ink-tertiary)]">
                      {incident.caption}
                    </p>

                    <div className="mt-4 border-t border-[var(--hairline)] pt-3">
                      <Kicker className="block">Actual against planned</Kicker>
                      <div className="mt-2 space-y-2">
                        <CostBar
                          label="As it happened"
                          pct={comparison.actualPct}
                          value={fmt$(comparison.actual)}
                          color="var(--crit)"
                        />
                        <CostBar
                          label="Replayed with the agents"
                          pct={comparison.plannedPct}
                          value={fmt$(comparison.planned)}
                          color="var(--good)"
                        />
                      </div>
                      <p className="mt-2 text-[11px] text-[var(--ink-tertiary)]">
                        Difference{' '}
                        <b className="mono font-semibold" style={{ color: 'var(--crit)' }}>
                          {fmt$(comparison.delta)}
                        </b>{' '}
                        · {MINES[incident.mine as MineCode]?.name ?? MSHORT[incident.mine as MineCode]}
                      </p>
                    </div>
                  </aside>
                </div>

                {/* the two timelines */}
                <div className="grid gap-4 lg:grid-cols-2">
                  <Commentable name="AsItHappened">
                    <section className="rounded-xl border border-[var(--hairline)] bg-[var(--surface-1)] p-4">
                      <h3 className="text-[13px] font-semibold text-[var(--ink-primary)]">
                        As it happened
                      </h3>
                      <p className="mb-2.5 mt-0.5 text-[11.5px] text-[var(--ink-tertiary)]">
                        Incident report, SAP PM / Maximo, Outlook thread, Teams debrief
                      </p>
                      <ol className="divide-y divide-[var(--hairline)]">
                        {incident.happened.map((moment, index) => (
                          <li
                            key={momentKey(moment.when, index)}
                            className="grid grid-cols-[62px_minmax(0,1fr)] gap-2.5 py-2 text-[12.5px]"
                          >
                            <time className="mono pt-px text-[11.5px] text-[var(--ink-tertiary)]">
                              {moment.when}
                            </time>
                            <div className="leading-relaxed text-[var(--ink-secondary)]">
                              {emphasisSegments(moment.text, moment.emphasis).map((segment, si) =>
                                segment.bold ? (
                                  <b key={si} className="font-semibold text-[var(--ink-primary)]">
                                    {segment.text}
                                  </b>
                                ) : (
                                  <span key={si}>{segment.text}</span>
                                ),
                              )}
                              {moment.links?.length ? (
                                <span className="ml-1.5 inline-flex flex-wrap gap-1.5">
                                  {moment.links.map((link) => (
                                    <Link
                                      key={link.openDoc}
                                      to={documentHref(link.openDoc)}
                                      title={link.openDoc}
                                      className="rounded border border-[var(--hairline)] px-1.5 py-0.5 text-[10.5px] font-medium text-[var(--accent)] transition-colors hover:border-[var(--accent)]"
                                    >
                                      {link.label}
                                    </Link>
                                  ))}
                                </span>
                              ) : null}
                            </div>
                          </li>
                        ))}
                      </ol>
                    </section>
                  </Commentable>

                  <Commentable name="AgentReplay">
                    <section
                      className="rounded-xl border p-4"
                      style={{
                        borderColor: 'var(--accent-soft)',
                        background: 'var(--surface-1)',
                      }}
                    >
                      <h3 className="text-[13px] font-semibold text-[var(--ink-primary)]">
                        Replayed with the agent chain
                      </h3>
                      <p className="mb-2.5 mt-0.5 text-[11.5px] text-[var(--ink-tertiary)]">
                        {incident.replaySub}
                      </p>
                      <ol className="divide-y" style={{ borderColor: 'var(--accent-soft)' }}>
                        {incident.replay.map((step, index) => (
                          <li
                            key={momentKey(`${step.when}::${step.agent}`, index)}
                            className="grid grid-cols-[62px_minmax(0,1fr)] gap-2.5 border-t-0 py-2 text-[12.5px] first:pt-0"
                            style={{ borderTopColor: 'var(--accent-soft)' }}
                          >
                            <time className="mono pt-px text-[11.5px] text-[var(--ink-tertiary)]">
                              {step.when}
                            </time>
                            <div className="leading-relaxed text-[var(--ink-secondary)]">
                              <span
                                className="mr-1.5 inline-block rounded px-1.5 text-[10.5px] font-medium"
                                style={{
                                  background: 'var(--accent-soft)',
                                  color: 'var(--accent)',
                                }}
                              >
                                {step.agent}
                              </span>
                              {step.text}
                            </div>
                          </li>
                        ))}
                      </ol>
                    </section>
                  </Commentable>
                </div>
              </article>
            </Commentable>
            )}

            {/* C1: the other incidents, as a chip row under the article. */}
            {onList || others.length === 0 ? null : (
              <Commentable name="IncidentPicker">
                <nav aria-label="Other incidents" className="flex flex-wrap items-center gap-2">
                  <span className="text-[11.5px] font-medium text-[var(--ink-tertiary)]">
                    {others.length} more incident{others.length === 1 ? '' : 's'}
                  </span>
                  {others.map((entry) => (
                    <Link
                      key={entry.incident.id}
                      to={`/issues/incidents/${entry.incident.id}`}
                      title={entry.incident.title}
                      className="inline-flex max-w-[22rem] items-center gap-2 rounded-full border border-[var(--hairline)] bg-[var(--surface-1)] px-3 py-1.5 text-[11.5px] transition-colors hover:border-[var(--accent)]"
                    >
                      <span className="mono shrink-0 font-medium text-[var(--ink-primary)]">
                        {entry.incident.machine}
                      </span>
                      <span className="truncate text-[var(--ink-secondary)]">
                        {entry.incident.title}
                      </span>
                    </Link>
                  ))}
                </nav>
              </Commentable>
            )}
          </SurfacePageBody>
        </div>
      </Commentable>
    </SurfacePageLayout>
  );
}

function CostBar({
  label,
  pct,
  value,
  color,
}: {
  label: string;
  pct: number;
  value: string;
  color: string;
}) {
  return (
    <div className="space-y-1">
      <div className="flex items-baseline justify-between gap-2 text-[11px]">
        <span className="text-[var(--ink-tertiary)]">{label}</span>
        <span className="mono font-semibold text-[var(--ink-primary)]">{value}</span>
      </div>
      <div className="h-2 overflow-hidden rounded bg-[var(--hairline)]">
        <i className="block h-full" style={{ width: `${pct}%`, background: color }} />
      </div>
    </div>
  );
}
